import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import RegularAvailabilitySlotWithBooking from '@discover-overrides/components/restaurant/list/item/regularAvailabilitySlot';
import isSlotAvailable from '@discover-data/stores/restaurant/utils/slots/isSlotAvailable';
import type { RestaurantToShow } from '@discover-data/stores/restaurant/model/types';
import useDate from '@discover-data/stores/hooks/date/useDate';
import useEndTime from '@discover-data/stores/hooks/times/end/useEndTime';
import usePeople from '@discover-data/stores/hooks/people/usePeople';
import useSession from '@discover-data/stores/hooks/session/useSession';
import useStartTime from '@discover-data/stores/hooks/times/start/useStartTime';
import AvailabilityCarousel, {
  AvailabilityCarouselPlaceholder,
  AvailabilityCarouselSkeleton,
} from '@discover-components/availability/carousel';
import AvailabilityCarouselItemSkeleton from '@discover-components/availability/carousel/item/skeleton';
import BackButton from '@discover-components/availability/slot/backButton';
import TimeEmptyMessage from '@discover-components/restaurant/filters/time/emptyMessage';
import getSlots from './getSlots';

export type RegularAvailabilityCarouselProps = {
  restaurant: RestaurantToShow;
};

export default function RegularAvailabilityCarousel({
  restaurant,
}: RegularAvailabilityCarouselProps) {
  const { regularAvailability, regularAvailabilityConfig, status, onHoldText } = restaurant;
  const [people] = usePeople();
  const [session] = useSession();
  const [date] = useDate();
  const [startTime] = useStartTime();
  const [endTime] = useEndTime();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const setSelectedDateAndScroll = useCallback(
    (newSelectedDate: string | null) => {
      // scroll to the start of the list
      if (scrollRef.current) {
        scrollRef.current.scroll({
          left: 0,
          behavior: 'smooth',
        });
      }

      setSelectedDate(newSelectedDate);
    },
    [setSelectedDate]
  );

  useEffect(() => {
    // reset the selected dates when the date or people change
    setSelectedDate(null);
  }, [date, people, session]);

  if (status === 'On Hold' && !!onHoldText) {
    return <AvailabilityCarouselPlaceholder>{onHoldText}</AvailabilityCarouselPlaceholder>;
  }

  if (!regularAvailability) {
    return <AvailabilityCarouselSkeleton />;
  }

  const sessionConfig = regularAvailabilityConfig?.[session];

  const slots = getSlots(
    regularAvailability,
    date,
    selectedDate,
    session,
    sessionConfig ? dayjs(sessionConfig.startTime) : undefined
  );

  if (!slots) {
    return <AvailabilityCarouselSkeleton />;
  }

  // every slot has no data or is unavailable or sold
  const isUnavailable = slots.every((slot) => slot === null || (slot && !isSlotAvailable(slot)));

  if (isUnavailable) {
    if (startTime || endTime) {
      return (
        <AvailabilityCarouselPlaceholder>
          <TimeEmptyMessage startTime={startTime} endTime={endTime} label="Regular Table" />
        </AvailabilityCarouselPlaceholder>
      );
    }

    return (
      <AvailabilityCarouselPlaceholder>
        All Regular Tables are sold out or unavailable
      </AvailabilityCarouselPlaceholder>
    );
  }

  const carouselItems = slots.map((slot, index) => {
    // undefined === still busy fetching
    if (slot === undefined) {
      return <AvailabilityCarouselItemSkeleton className="size-full lg:size-full" key={index} />;
    }

    return (
      <RegularAvailabilitySlotWithBooking
        key={`${slot.date}-${slot.time}`}
        restaurant={restaurant}
        slot={slot}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDateAndScroll}
      />
    );
  });

  if (date === 'week' && selectedDate) {
    carouselItems.unshift(<BackButton key="back" onClick={() => setSelectedDateAndScroll(null)} />);
  }

  return <AvailabilityCarousel>{carouselItems}</AvailabilityCarousel>;
}
